/**
 * Brand v2 Component Contract
 * --------------------------------
 * Component: EmptyState — one box for "nothing here yet", "loading" and "note"
 *
 * למה זה קיים (2026-09-18): כל מסך בנה לעצמו תיבת "אין עדיין…" משלו, בגוונים
 * אחרים ובריווח אחר. זו התיבה האחת. docs/BRAND.md §Components — one home.
 *
 * 1. Tokens consumed:
 *    - Foreground: --fg-default (כותרת), --fg-muted (גוף), --fg-faint (אייקון)
 *    - Surface:    .card
 *    - Role:       .text-warning (tone="warning")
 * 2. States supported: default (tone="neutral") · warning · loading (aria-busy)
 * 3. RTL safety: לוגי בלבד (text-start, gap), בלי left/right.
 * 4. Motion preset: אין.
 * 5. Accessibility: role="status" — קורא מסך מקריא שינוי מצב.
 * 6. Skin compatibility: Quiet / Pro / Signature: untested
 * 7. Theme compatibility: Dark / GAM Light: untested
 */

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

export type EmptyStateTone = "neutral" | "warning";

export interface EmptyStateProps {
  title?: ReactNode;
  children?: ReactNode;
  icon?: LucideIcon;
  tone?: EmptyStateTone;
  /** Shows the box as a loading placeholder (aria-busy). */
  loading?: boolean;
  /** Optional call-to-action row under the text. */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  title,
  children,
  icon: Icon,
  tone = "neutral",
  loading = false,
  action,
  className = "",
}: EmptyStateProps) {
  const toneClass = tone === "warning" ? "text-warning" : "text-[var(--fg-muted)]";
  return (
    <div
      role="status"
      aria-busy={loading || undefined}
      className={`card flex items-start gap-3 rounded-xl p-4 text-start text-sm ${toneClass} ${className}`}
    >
      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fg-faint)]" aria-hidden />}
      <div className="min-w-0 space-y-1">
        {title && <div className="font-medium text-[var(--fg-default)]">{title}</div>}
        {children && <div>{children}</div>}
        {action && <div className="pt-1">{action}</div>}
      </div>
    </div>
  );
}
